import React, { useEffect, useRef } from 'react';

declare const THREE: any;

export function Component() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // --- DYNAMIC SCRIPT LOADING ---
    const loadScripts = async () => {
      const loadScript = (src: string, globalName: string) => new Promise<void>((res, rej) => {
        if ((window as any)[globalName]) { res(); return; }
        if (document.querySelector(`script[src="${src}"]`)) {
          const check = setInterval(() => {
            if ((window as any)[globalName]) { clearInterval(check); res(); }
          }, 50);
          setTimeout(() => { clearInterval(check); rej(new Error(`Timeout waiting for ${globalName}`)); }, 10000);
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = () => { setTimeout(() => res(), 100); };
        s.onerror = () => rej(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
      });

      try {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js', 'THREE');
      } catch (e) {
        console.error('Failed to load base scripts:', e);
      }

      initApplication();
    };

    const initApplication = async () => {
      const SLIDER_CONFIG: any = {
        settings: {
          transitionDuration: 2.5, autoSlideSpeed: 5000, currentEffect: "glass",
          globalIntensity: 1.0, speedMultiplier: 1.0, distortionStrength: 1.0, colorEnhancement: 1.0,
          glassRefractionStrength: 0.8, glassChromaticAberration: 0.5, glassBubbleClarity: 1.2, glassEdgeGlow: 0.8, glassLiquidFlow: 1.0
        }
      };

      let currentSlideIndex = 0;
      let isTransitioning = false;
      let shaderMaterial: any, renderer: any, scene: any, camera: any;
      let slideTextures: any[] = [];
      let texturesLoaded = false;
      let autoSlideTimer: any = null;
      let progressAnimation: any = null;
      let sliderEnabled = false;

      const SLIDE_DURATION = () => SLIDER_CONFIG.settings.autoSlideSpeed;
      const PROGRESS_UPDATE_INTERVAL = 50;
      const TRANSITION_DURATION = () => SLIDER_CONFIG.settings.transitionDuration;

       const slides = [
        { title: "Corporate Meeting", media: "/assets/images/gallery/year-2026/summer-meet-2026/1.webp" },
        // { title: "Business Strategy", media: "/assets/images/gallery/year-2026/summer-meet-2026/2.webp" },
        { title: "Business Strategy", media: "/assets/images/gallery/year-2026/summer-meet-2026/3.webp" },
        { title: "Business Strategy", media: "/assets/images/gallery/year-2026/summer-meet-2026/8.webp" },
        // { title: "Business Strategy", media: "/assets/images/gallery/year-2026/summer-meet-2026/9.webp" },
        { title: "Business Strategy", media: "/assets/images/gallery/year-2026/summer-meet-2026/10.webp" },
        // { title: "Business Strategy", media: "/assets/images/gallery/year-2025/Speaker/Speaker-15.jpg" }

      ];
      const vertexShader = `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
      const fragmentShader = `
            uniform sampler2D uTexture1, uTexture2;
            uniform float uProgress;
            uniform float uTime;
            uniform vec2 uResolution, uTexture1Size, uTexture2Size;
            uniform float uGlobalIntensity, uSpeedMultiplier, uDistortionStrength;
            uniform float uGlassRefractionStrength, uGlassChromaticAberration, uGlassBubbleClarity, uGlassEdgeGlow, uGlassLiquidFlow;
            varying vec2 vUv;

            vec2 getCoverUV(vec2 uv, vec2 textureSize, float timeOffset) {
                vec2 s = uResolution / textureSize;
                float scale = max(s.x, s.y) * 1.12;
                vec2 scaledSize = textureSize * scale;
                vec2 offset = (uResolution - scaledSize) * 0.5;
                offset.x += sin(timeOffset * 0.18) * (scaledSize.x * 0.05);
                return (uv * uResolution - offset) / scaledSize;
            }
            
            vec4 glassEffect(vec2 uv, float progress) {
                float time = progress * 5.0 * uSpeedMultiplier;
                vec2 uv1 = getCoverUV(uv, uTexture1Size, uTime); vec2 uv2 = getCoverUV(uv, uTexture2Size, uTime);
                float maxR = length(uResolution) * 0.85; float br = progress * maxR;
                vec2 p = uv * uResolution; vec2 c = uResolution * 0.5;
                float d = length(p - c); float nd = d / max(br, 0.001);
                float param = smoothstep(br + 3.0, br - 3.0, d);
                vec4 img;
                if (param > 0.0) {
                     float ro = 0.08 * uGlassRefractionStrength * uDistortionStrength * uGlobalIntensity * pow(smoothstep(0.3 * uGlassBubbleClarity, 1.0, nd), 1.5);
                     vec2 dir = (d > 0.0) ? (p - c) / d : vec2(0.0);
                     vec2 distUV = uv2 - dir * ro;
                     distUV += vec2(sin(time + nd * 10.0), cos(time * 0.8 + nd * 8.0)) * 0.015 * uGlassLiquidFlow * uSpeedMultiplier * nd * param;
                     float ca = 0.02 * uGlassChromaticAberration * uGlobalIntensity * pow(smoothstep(0.3, 1.0, nd), 1.2);
                     img = vec4(texture2D(uTexture2, distUV + dir * ca * 1.2).r, texture2D(uTexture2, distUV + dir * ca * 0.2).g, texture2D(uTexture2, distUV - dir * ca * 0.8).b, 1.0);
                     if (uGlassEdgeGlow > 0.0) {
                        float rim = smoothstep(0.95, 1.0, nd) * (1.0 - smoothstep(1.0, 1.01, nd));
                        img.rgb += rim * 0.08 * uGlassEdgeGlow * uGlobalIntensity;
                     }
                } else { img = texture2D(uTexture2, uv2); }
                vec4 oldImg = texture2D(uTexture1, uv1);
                if (progress > 0.95) img = mix(img, texture2D(uTexture2, uv2), (progress - 0.95) / 0.05);
                return mix(oldImg, img, param);
            }

            void main() {
                gl_FragColor = glassEffect(vUv, uProgress);
            }
        `;

      const updateShaderUniforms = () => {
        if (!shaderMaterial) return;
        const s = SLIDER_CONFIG.settings, u = shaderMaterial.uniforms;
        for (const key in s) {
          const uName = 'u' + key.charAt(0).toUpperCase() + key.slice(1);
          if (u[uName]) u[uName].value = s[key];
        }
      };

      const animateProgress = (targetProgress: number, duration: number, onComplete: () => void) => {
        const startProgress = shaderMaterial.uniforms.uProgress.value;
        const startTime = performance.now();

        const animate = (currentTime: number) => {
          const elapsed = (currentTime - startTime) / 1000;
          const t = Math.min(elapsed / duration, 1.0);

          // easing function (easeOutQuad)
          const easeT = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

          shaderMaterial.uniforms.uProgress.value = startProgress + (targetProgress - startProgress) * easeT;

          if (t < 1.0) {
            requestAnimationFrame(animate);
          } else {
            onComplete();
          }
        };
        requestAnimationFrame(animate);
      };

      const navigateToSlide = (targetIndex: number) => {
        if (isTransitioning || targetIndex === currentSlideIndex) return;
        stopAutoSlideTimer();
        quickResetProgress(currentSlideIndex);

        const currentTexture = slideTextures[currentSlideIndex];
        const targetTexture = slideTextures[targetIndex];
        if (!currentTexture || !targetTexture) return;

        isTransitioning = true;
        shaderMaterial.uniforms.uTexture1.value = currentTexture;
        shaderMaterial.uniforms.uTexture2.value = targetTexture;
        shaderMaterial.uniforms.uTexture1Size.value = currentTexture.userData.size;
        shaderMaterial.uniforms.uTexture2Size.value = targetTexture.userData.size;
        shaderMaterial.uniforms.uProgress.value = 0;

        currentSlideIndex = targetIndex;
        updateNavigationState(currentSlideIndex);

        animateProgress(1.0, TRANSITION_DURATION(), () => {
          shaderMaterial.uniforms.uProgress.value = 0;
          shaderMaterial.uniforms.uTexture1.value = targetTexture;
          shaderMaterial.uniforms.uTexture1Size.value = targetTexture.userData.size;
          isTransitioning = false;
        });
        safeStartTimer(0);
      };

      const handleSlideChange = () => {
        if (isTransitioning || !texturesLoaded || !sliderEnabled) return;
        navigateToSlide((currentSlideIndex + 1) % slides.length);
      };

      const createSlidesNavigation = () => {
        const nav = document.getElementById("slidesNav"); if (!nav) return;
        nav.innerHTML = "";
        slides.forEach((slide, i) => {
          const item = document.createElement("div");
          item.className = `lumina-slide-nav-item${i === 0 ? " active" : ""}`;
          item.dataset.slideIndex = String(i);
          item.innerHTML = `<div class="lumina-slide-progress-line"><div class="lumina-slide-progress-fill"></div></div><div class="lumina-slide-nav-title">${slide.title}</div>`;
          item.addEventListener("click", (e) => {
            e.stopPropagation();
            if (!isTransitioning && i !== currentSlideIndex) {
              stopAutoSlideTimer();
              quickResetProgress(currentSlideIndex);
              navigateToSlide(i);
            }
          });
          nav.appendChild(item);
        });
      };

      const updateNavigationState = (idx: number) => document.querySelectorAll(".lumina-slide-nav-item").forEach((el, i) => el.classList.toggle("active", i === idx));
      const updateSlideProgress = (idx: number, prog: number) => { const el = document.querySelectorAll(".lumina-slide-nav-item")[idx]?.querySelector(".lumina-slide-progress-fill") as HTMLElement; if (el) { el.style.width = `${prog}%`; el.style.opacity = '1'; } };
      const fadeSlideProgress = (idx: number) => { };
      const quickResetProgress = (idx: number) => { const el = document.querySelectorAll(".lumina-slide-nav-item")[idx]?.querySelector(".lumina-slide-progress-fill") as HTMLElement; if (el) { el.style.transition = "none"; el.style.width = "0%"; } };

      const startAutoSlideTimer = () => {
        if (!texturesLoaded || !sliderEnabled) return;
        stopAutoSlideTimer();
        
        // Use a tiny delay to ensure the DOM has applied any 0% width resets
        autoSlideTimer = setTimeout(() => {
          const el = document.querySelectorAll(".lumina-slide-nav-item")[currentSlideIndex]?.querySelector(".lumina-slide-progress-fill") as HTMLElement;
          if (el) {
            el.style.transition = `width ${SLIDE_DURATION()}ms linear`;
            el.style.width = "100%";
            el.style.opacity = '1';
          }

          progressAnimation = setTimeout(() => {
            fadeSlideProgress(currentSlideIndex);
            if (!isTransitioning) handleSlideChange();
          }, SLIDE_DURATION());
        }, 50);
      };
      const stopAutoSlideTimer = () => { if (progressAnimation) clearTimeout(progressAnimation); if (autoSlideTimer) clearTimeout(autoSlideTimer); progressAnimation = null; autoSlideTimer = null; };
      const safeStartTimer = (delay = 0) => { stopAutoSlideTimer(); if (sliderEnabled && texturesLoaded) { if (delay > 0) autoSlideTimer = setTimeout(startAutoSlideTimer, delay); else startAutoSlideTimer(); } };

      const loadImageTexture = (src: string) => new Promise<any>((resolve, reject) => {
        const l = new THREE.TextureLoader();
        l.load(src, (t: any) => { t.minFilter = t.magFilter = THREE.LinearFilter; t.userData = { size: new THREE.Vector2(t.image.width, t.image.height) }; resolve(t); }, undefined, reject);
      });

      const initRenderer = async () => {
        const canvas = document.querySelector(".webgl-canvas") as HTMLCanvasElement; if (!canvas) return;
        scene = new THREE.Scene(); camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false });
        const width = canvas.parentElement?.clientWidth || window.innerWidth;
        const height = canvas.parentElement?.clientHeight || window.innerHeight;

        renderer.setSize(width, height); renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        shaderMaterial = new THREE.ShaderMaterial({
          uniforms: {
            uTexture1: { value: null }, uTexture2: { value: null }, uProgress: { value: 0 }, uTime: { value: 0 },
            uResolution: { value: new THREE.Vector2(width, height) },
            uTexture1Size: { value: new THREE.Vector2(1, 1) }, uTexture2Size: { value: new THREE.Vector2(1, 1) },
            uGlobalIntensity: { value: 1.0 }, uSpeedMultiplier: { value: 1.0 }, uDistortionStrength: { value: 1.0 },
            uGlassRefractionStrength: { value: 1.0 }, uGlassChromaticAberration: { value: 1.0 }, uGlassBubbleClarity: { value: 1.0 }, uGlassEdgeGlow: { value: 1.0 }, uGlassLiquidFlow: { value: 1.0 }
          },
          vertexShader, fragmentShader
        });
        scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), shaderMaterial));

        for (const s of slides) { try { slideTextures.push(await loadImageTexture(s.media)); } catch { console.warn("Failed texture"); } }
        if (slideTextures.length >= 2) {
          shaderMaterial.uniforms.uTexture1.value = slideTextures[0];
          shaderMaterial.uniforms.uTexture2.value = slideTextures[1];
          shaderMaterial.uniforms.uTexture1Size.value = slideTextures[0].userData.size;
          shaderMaterial.uniforms.uTexture2Size.value = slideTextures[1].userData.size;
          texturesLoaded = true; sliderEnabled = true;
          updateShaderUniforms(); // Apply config
          safeStartTimer(500);
        }

        const startTime = Date.now();
        const render = () => { 
          requestAnimationFrame(render); 
          if (shaderMaterial) shaderMaterial.uniforms.uTime.value = (Date.now() - startTime) / 1000.0;
          renderer.render(scene, camera); 
        };
        render();
      };

      createSlidesNavigation();
      initRenderer();

      const visHandler = () => document.hidden ? stopAutoSlideTimer() : (!isTransitioning && safeStartTimer());
      const resizeHandler = () => {
        if (renderer) {
          const canvas = document.querySelector(".webgl-canvas") as HTMLCanvasElement;
          const width = canvas?.parentElement?.clientWidth || window.innerWidth;
          const height = canvas?.parentElement?.clientHeight || window.innerHeight;
          renderer.setSize(width, height);
          shaderMaterial.uniforms.uResolution.value.set(width, height);
        }
      };

      document.addEventListener("visibilitychange", visHandler);
      window.addEventListener("resize", resizeHandler);

      return () => {
        stopAutoSlideTimer();
        document.removeEventListener("visibilitychange", visHandler);
        window.removeEventListener("resize", resizeHandler);
      };
    };

    let cleanupFunc: (() => void) | null = null;
    loadScripts().then(async () => {
      cleanupFunc = await initApplication();
    });
    return () => {
      if (cleanupFunc) cleanupFunc();
    };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', width: '100%' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .lumina-slider-wrapper {
          position: relative;
          width: 100%;
          flex: 1;
          overflow: hidden;
          background-color: #000;
          color: #fff;
        }
        .lumina-webgl-canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          pointer-events: none;
          z-index: 0;
        }
        .lumina-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          padding-bottom: 3rem;
          z-index: 10;
          text-align: center;
          background: linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.6) 100%);
        }
        .lumina-card {
          position: relative;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          padding: 2rem 3rem;
          border-radius: 1.5rem;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255,255,255,0.8);
          max-width: 600px;
          margin: 0;
        }
        .lumina-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 6px;
          background: linear-gradient(90deg, #f59e0b, #ef4444);
          border-top-left-radius: 1.5rem;
          border-top-right-radius: 1.5rem;
        }
        .lumina-title {
          font-family: var(--font-display, 'Georgia', serif);
          font-size: clamp(2rem, 4vw, 3.5rem);
          font-weight: 800;
          color: #111827;
          margin-bottom: 0.5rem;
          line-height: 1.1;
          letter-spacing: -0.02em;
        }
        .lumina-subtitle {
          font-family: var(--font-sans, 'system-ui', sans-serif);
          font-size: clamp(1rem, 1.5vw, 1.15rem);
          color: #4b5563;
          font-weight: 500;
          letter-spacing: 0.02em;
        }
        .lumina-slides-navigation {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          gap: 0;
          z-index: 20;
          width: 100%;
        }
        .lumina-slide-nav-item {
          flex: 1;
          cursor: pointer;
          padding: 0;
        }
        .lumina-slide-progress-line {
          height: 3px;
          background: rgba(255, 255, 255, 0.3);
          position: relative;
          border-radius: 0;
        }
        .lumina-slide-progress-fill {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: #ffffff;
          width: 0%;
          opacity: 0;
          border-radius: 0;
        }
        .lumina-slide-nav-title {
          display: none;
        }
      ` }} />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style dangerouslySetInnerHTML={{
        __html: `
          .sm-banner-section {
            padding: 0;
            font-family: 'Inter', sans-serif;
          }

          .sm-banner-outer {
            position: relative;
            max-width: 1200px;
            margin: 0 2rem;
            width: calc(100% - 4rem);
            z-index: 10;
          }

          .sm-banner-wrap {
            background: linear-gradient(135deg, #FFF8ED 0%, #FFF3D6 50%, #FFF0E0 100%);
            border-top: 1.5px solid #FACF7A;
            border-bottom: 1.5px solid #FACF7A;
            overflow: hidden;
            position: relative;
            min-height: 120px;
            width: 100%;
          }

          .sm-banner-container {
            max-width: 1100px;
            margin: 0 auto;
            position: relative;
          }

          .sm-deco-circle {
            position: absolute;
            border-radius: 50%;
            opacity: 0.18;
          }

          .sm-inner {
            display: flex;
            align-items: stretch;
            position: relative;
            z-index: 2;
            width: 100%;
          }

          .sm-split-panel {
            flex: 1;
            padding: 16px 36px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            width: 100%;
          }

          .sm-title-group {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }

          .sm-right-group {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            text-align: right;
          }

          .sm-title-row {
            display: flex;
            align-items: baseline;
            justify-content: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .sm-title-row-first {
            margin-bottom: -6px;
          }

          .sm-word-summer {
            font-family: 'Playfair Display', Georgia, serif;
            font-size: 46px;
            font-weight: 700;
            color: #E8851A;
            line-height: 1;
          }

          .sm-word-members, .sm-word-meet, .sm-word-year {
            font-size: 34px;
            font-weight: 700;
            color: #1A1A1A;
            line-height: 1;
          }

          .sm-word-year { color: #E8851A; }

          .sm-tagline {
            font-size: 16px;
            color: #7A5E30;
            font-weight: 500;
            letter-spacing: 0.5px;
            margin-top: 4px;
          }

          .sm-sponsor-pill {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            gap: 5px;
            background: #fff;
            border: 1.5px solid #F0C060;
            border-radius: 16px;
            padding: 10px 18px 8px;
            margin-top: 12px;
            width: fit-content;
          }

          .sm-sponsor-logo { height: 20px; width: auto; }

          .sm-sponsor-label {
            font-size: 9px;
            font-weight: 600;
            color: #B87A10;
            letter-spacing: 1.5px;
            text-transform: uppercase;
          }

          .sm-deco-lemon, .sm-deco-leaf, .sm-deco-sun-ring {
            position: absolute;
            pointer-events: none;
            z-index: 1;
          }

          @keyframes sm-float1 { 0%,100%{transform:translateY(0) rotate(-10deg)} 50%{transform:translateY(-6px) rotate(-8deg)} }
          @keyframes sm-float2 { 0%,100%{transform:translateY(0) rotate(15deg)} 50%{transform:translateY(-8px) rotate(17deg)} }
          @keyframes sm-pulse-ring { 0%,100%{opacity:0.12} 50%{opacity:0.22} }

          .sm-anim-float1 { animation: sm-float1 3.8s ease-in-out infinite; }
          .sm-anim-float2 { animation: sm-float2 4.5s ease-in-out infinite; }
          .sm-anim-pulse { animation: sm-pulse-ring 3s ease-in-out infinite; }

          @media (max-width: 768px) {
            .sm-banner-outer {
              margin: 0 1rem !important;
              width: calc(100% - 2rem) !important;
            }
            .sm-banner-wrap {
              border-radius: 1.2rem !important;
            }
            .sm-banner-content {
              flex-direction: column !important;
              padding: 12px 16px !important;
              gap: 8px !important;
              text-align: center;
            }
            .sm-card-middle {
              align-items: center !important;
              padding-left: 0 !important;
              text-align: center !important;
            }
            .sm-word-summer { font-size: 28px !important; }
            .sm-title-row-first { margin-bottom: 4px !important; }
            .sm-word-members, .sm-word-year { font-size: 18px !important; }
            .sm-tagline { font-size: 14px !important; }
            .sm-tagline-desc { font-size: 11.5px !important; margin-top: 2px !important; }
            .sm-sponsor-pill { padding: 6px 12px !important; margin-top: 2px !important; gap: 8px !important; }
            .sm-sponsor-logo { height: 16px !important; }
            .sm-sponsor-label { font-size: 8px !important; }
            .lumina-overlay { padding-bottom: 2rem !important; }
          }
        ` }} />

      <main className="lumina-slider-wrapper" ref={containerRef}>
        <canvas className="lumina-webgl-canvas webgl-canvas"></canvas>

        <div className="lumina-overlay">
          <div className="sm-banner-outer">
            <div className="sm-banner-wrap" style={{
              border: '1.5px solid #FACF7A',
              borderRadius: '1.5rem',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4), inset 0 0 0 1px rgba(255,255,255,0.4)',
              width: '100%',
              minHeight: 'auto',
              margin: 0
            }}>
              <div className="sm-deco-circle sm-anim-pulse" style={{ width: "220px", height: "220px", background: "#F5A623", top: "-70px", right: "30px" }}></div>
              <div className="sm-deco-circle" style={{ width: "130px", height: "130px", background: "#FBA834", bottom: "-40px", left: "20px" }}></div>
              <div className="sm-deco-circle" style={{ width: "80px", height: "80px", background: "#FFD580", top: "20px", left: "180px", opacity: 0.25 }}></div>



              <div className="sm-deco-sun-ring sm-anim-pulse" style={{ top: "-18px", left: "-18px", opacity: 0.18 }}>
                <svg width="90" height="90" viewBox="0 0 90 90" fill="none"><circle cx="45" cy="45" r="38" stroke="#FBA834" strokeWidth="3" strokeDasharray="8 5" /></svg>
              </div>



              <div className="sm-banner-content" style={{ position: 'relative', zIndex: 2, padding: '24px 40px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div className="sm-title-row sm-title-row-first"><span className="sm-word-summer" style={{ fontSize: '42px' }}>Summer</span></div>
                  <div className="sm-title-row">
                    <span className="sm-word-members" style={{ fontSize: '32px' }}>Members Meet </span>
                    <span className="sm-word-year" style={{ fontSize: '32px', marginLeft: '6px' }}>2026</span>
                  </div>
                </div>

                <div className="sm-card-middle" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, paddingLeft: '20px' }}>
                  {/* <div className="sm-tagline" style={{ color: '#1A1A1A', fontSize: '20px', fontWeight: 600 }}>Thank you for members for an <br/> incredible evening!</div> */}
                  <div className="sm-tagline" style={{ color: '#1A1A1A', fontSize: '20px', fontWeight: 600 }}>Thank You for Making the MSB Association Summer Members Meet 2026 a Huge Success!</div>
                  {/* <p className="sm-tagline-desc" style={{ color: '#7A5E30', fontSize: '14px', marginTop: '6px', margin: 0, lineHeight: '1.4' }}>
                  Relive the moments • Stay connected • See you at the next one.
                </p> */}
                </div>

                <div className="sm-sponsor-pill" style={{ margin: 0, padding: '12px 24px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px', borderRadius: '100px', background: '#fff', border: '1.5px solid #F0C060', boxShadow: '0 4px 12px rgba(240, 192, 96, 0.15)' }}>
                  <img src="https://msbassociationuk.org/assets/images/peratera-logo-black.svg" alt="Peratera" className="sm-sponsor-logo" style={{ height: '24px' }} />
                  <div style={{ width: '1px', height: '24px', background: '#F0C060' }}></div>
                  <span className="sm-sponsor-label" style={{ fontSize: '11px', fontWeight: 700, color: '#B87A10', letterSpacing: '1px' }}>EVENT SPONSOR</span>
                </div>
              </div>
            </div>

           <div style={{ position: 'absolute', top: '-36px', right: '30px', zIndex: 20, width: '170px', height: '36px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '8px' }}>
              <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1, overflow: 'visible' }} viewBox="0 0 170 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="tab_grad" x1="0" y1="0" x2="170" y2="0" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FFF4DB" />
                    <stop offset="1" stopColor="#FFF0E0" />
                  </linearGradient>
                </defs>
                <rect x="2" y="35" width="166" height="2.5" fill="url(#tab_grad)" />
                <path d="M 0 35.75 C 12 35.75 16 1.5 28 1.5 L 142 1.5 C 154 1.5 158 35.75 170 35.75" fill="url(#tab_grad)" stroke="#FACF7A" strokeWidth="1.5" />
              </svg>
              <span style={{ position: 'relative', zIndex: 1, color: '#B87A10', fontSize: '10px', fontWeight: 700, fontFamily: 'Inter, sans-serif', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Friday, 26th June
              </span>
            </div>

          </div>
        </div>

        <nav className="lumina-slides-navigation" id="slidesNav"></nav>
      </main>
    </div>
  );
}
